import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import type { FormManagementWorkspaceData } from '@/contracts/form-management'
import type { FormFolderContract, FormUiContract } from '@/contracts/questionnaire'
import { Archive, ArchiveRestore, Lock } from 'lucide-react'

type FormManagementOperationsProps = {
  form: FormUiContract
  folders: FormFolderContract[]
  workspaceData: FormManagementWorkspaceData
  canManageForms: boolean
  onArchiveToggle: (status: 'archived' | 'draft') => void
}

export function FormManagementOperations({
  form,
  workspaceData,
  canManageForms,
  onArchiveToggle,
}: FormManagementOperationsProps) {
  const action = workspaceData.operationActions.find((item) => item.id === 'archive')
  const archived = form.status === 'archived'
  const Icon = archived ? ArchiveRestore : Archive
  const nextStatus = archived ? 'draft' : 'archived'
  const actionLabel = archived ? '還原封存' : '封存問卷'
  const confirmLabel = archived ? '確認還原' : '確認封存'

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge>Mock-safe</Badge>
        <Badge variant="outline">local form lifecycle</Badge>
        {!canManageForms && (
          <Badge variant="secondary" className="gap-1">
            <Lock className="size-3" />
            只讀檢視
          </Badge>
        )}
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <Card>
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-950">操作設定</h3>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              此區只保留封存與還原封存；其他問卷操作已從目前介面移除，且不呼叫外部後端。
            </p>

            <div className="mt-5 rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="font-medium text-slate-950">目前問卷狀態</div>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    {action?.description ??
                      (archived
                        ? '將此問卷還原為草稿狀態。'
                        : '封存後此問卷不能透過公開填寫路由填答。')}
                  </p>
                </div>
                <Badge variant={canManageForms ? 'success' : 'secondary'}>
                  {canManageForms ? '可操作' : '需要管理員權限'}
                </Badge>
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" className="mt-4" variant="outline" disabled={!canManageForms}>
                    <Icon className="size-4" />
                    {actionLabel}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {action?.confirmationTitle ?? (archived ? '還原這份問卷？' : '封存這份問卷？')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {action?.confirmationDescription ??
                        (archived
                          ? '還原後會回到草稿狀態，可再切換為公開。'
                          : '封存後此問卷無法填寫，仍可從封存篩選查看。')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">
                    問卷：{form.title}
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel>取消</AlertDialogCancel>
                    <AlertDialogAction asChild>
                      <Button type="button" onClick={() => onArchiveToggle(nextStatus)}>
                        {confirmLabel}
                      </Button>
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-950">狀態說明</h3>
            <div className="mt-4 space-y-3">
              <div className="rounded-md bg-slate-50 p-3">
                <div className="text-xs font-medium text-slate-500">問卷</div>
                <div className="mt-1 text-sm font-medium text-slate-900">{form.title}</div>
              </div>
              <div className="rounded-md bg-slate-50 p-3">
                <div className="text-xs font-medium text-slate-500">目前狀態</div>
                <div className="mt-1 text-sm font-medium text-slate-900">
                  {archived ? '已封存' : form.status === 'published' ? '公開' : '草稿'}
                </div>
              </div>
              <div className="rounded-md bg-indigo-50 p-3 text-sm leading-6 text-indigo-800">
                草稿與已封存問卷都不能填寫；公開問卷才會在 `/to/:formId` 顯示填答畫面。
              </div>
              {!canManageForms && (
                <div className="rounded-md bg-slate-100 p-3 text-sm leading-6 text-slate-600">
                  目前角色只能檢視管理工作區，不能封存或還原問卷。
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
